import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { baseURL } from '../../../api';
import { Global_Data } from '../../../globalData/GlobalData';
import { vsprintf } from "sprintf-js";

const ColumnChildAutoComplete = ({ params, item, handleRowUpdate, cellError }) => {
  const { token, textValue } = Global_Data();
  const displayField = item?.data?.sDisplayField || '';
  const displayFormat = item?.data?.sDisplayFormat || '';
  const valueField = item?.data?.sValueField;
  const colId = item?.inputtable?.sColumnID;
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        let uri = item?.data?.sDataSource;
        if (!uri) return;

        // Replace placeholders like {fieldName} with values from textValue
        if (uri.includes("{") && uri.includes("}")) {
          for (const key in textValue) {
            if (textValue.hasOwnProperty(key)) {
              const regex = new RegExp(`\\{${key}\\}`, "g");
              uri = uri.replace(regex, textValue[key] || "");
            }
          }
        }
        // If there are still unresolved placeholders, skip fetching
        if (uri.includes("{") && uri.includes("}")) return;

        const fullUrl = uri.startsWith("/") ? `${baseURL}${uri}` : `${baseURL}/${uri}`;
        const response = await fetch(fullUrl, {
          method: "GET",
          headers: {
            Accept: "*/*",
            Authorization: `Bearer ${token}`,
          },
        });
        const result = await response.json();
        const records = result?.data?.records || result?.data?.data || result?.data || [];
        setOptions(Array.isArray(records) ? records : []);
      } catch (error) {
        console.error("ColumnChildAutoComplete fetch error:", error);
      }
    };

    fetchOptions();
  }, [item?.data?.sDataSource, token, textValue]);

  const processedOptions = useMemo(() => {
    return options.map(opt => {
      const keys = [...(displayField.matchAll(/{(.*?)}/g) || [])].map(m => m[1]);
      const args = keys.map(k => {
        const v = opt[k];
        return v === undefined || v === null ? "" : String(v);
      });
      let _display = "";
      try {
        _display = vsprintf(displayFormat ?? "%s", args);
      } catch (e) {
        _display = args.join(", ");
      }
      return { ...opt, _display };
    });
  }, [options, displayField, displayFormat]);

  const selectedValue = useMemo(() => {
    const rowVal = params?.row?.[colId];
    if (rowVal == null) return null;
    return processedOptions.find(opt => opt?.[valueField] === rowVal) || null;
  }, [params?.row, colId, processedOptions, valueField]);

  const [internalValue, setInternalValue] = useState(() => selectedValue || null);

  useEffect(() => {
    if (!open) {
      setInternalValue(selectedValue || null);
    }
  }, [selectedValue, open]);

  const filterOptions = useCallback((options, state) => {
    const search = (state.inputValue || "").toLowerCase();
    return options.filter(opt =>
      (opt._display || '').toLowerCase().includes(search)
    );
  }, []);

  const handleChange = (e, newValue) => {
    setInternalValue(newValue);

    if (!newValue) {
      handleRowUpdate({
        fieldName: colId,
        value: null,
        rowId: params?.row?.id,
      });
      return;
    }

    handleRowUpdate({
      fieldName: colId,
      value: newValue?.[valueField],
      rowId: params?.row?.id,
    });
  };

  const fieldId = params?.row?._fieldIds?.[colId] || item?.component?.sName || colId;

  return (
    <Autocomplete
      options={processedOptions}
      name={item?.component?.sName}
      id={`${fieldId}-${params?.row?.id}`}
      value={internalValue}
      onChange={handleChange}
      getOptionLabel={(option) => option?._display || ''}
      isOptionEqualToValue={(option, value) =>
        option?.[valueField] === value?.[valueField]
      }
      onOpen={() => setOpen(true)}
      onClose={() => {
        setOpen(false);
        setInternalValue(selectedValue || null);
      }}
      renderInput={(inputParams) => {
        const { inputProps, ...restParams } = inputParams;
        const { onKeyDown: originalOnKeyDown, value: inputValueFromParams, ...restInputProps } = inputProps || {};

        const wrappedInputProps = {
          ...restInputProps,
          value: typeof inputValueFromParams === 'string'
            ? inputValueFromParams.replace(/<[^>]+>/g, "")
            : inputValueFromParams,
          onKeyDown: (e) => {
            if (typeof originalOnKeyDown === 'function') {
              originalOnKeyDown(e);
            }
            const key = e.key || '';
            const code = e.code || '';
            if (key === ' ' || key === 'Spacebar' || code === 'Space') {
              e.stopPropagation();
            }
          }
        };

        return (
          <TextField
            {...restParams}
            inputProps={wrappedInputProps}
            placeholder={`Select ${item?.inputtable?.sHeader || ''}`}
            error={!!cellError}
            helperText={cellError || ""}
            size="medium"
          />
        );
      }}
      clearIcon={<CloseIcon fontSize="small" />}
      filterOptions={filterOptions}
      {...item?.component?.sProps}
    />
  );
};

export default ColumnChildAutoComplete;
